import type { JSX } from '@builder.io/qwik';
import { useContextProvider } from '@builder.io/qwik';
import { component$, useContext, useSignal } from '@builder.io/qwik';
import { AppDataContext } from '~/contexts/AppDataContext';
import { NavContext } from '~/contexts/NavContext';
import MobileNav from '~/components/Layout/Header/Mobile/MobileNav';
import { cva } from 'class-variance-authority';
import {
  getCSSColorBg,
  getCSSHeightSmFactor,
  getCSSHeightMdFactor,
  getCSSHeightLgFactor,
} from '~/utilities/getCSS';
import Nav from '~/components/Layout/Header/Desktop/Nav';
import AccessibilityButton from '~/components/Shared/Accessibility/AccessibilityButton';
import LogoImageSI from '~/components/Shared/Logo/LogoImageSI';
import MobileIconSI from '~/components/Layout/Header/Mobile/MobileIconSI';
import { SI_PRIMARY_NAV_THEME } from '~/constants/SI_PRIMARY_NAV_THEME';
import LanguagePicker from '~/components/Shared/Accessibility/LanguagePicker';
import SearchButton from './SearchButton';
import type { NavThemeComponentsInterface } from '~/types/AppData/Theme/NavThemeComponentsInterface';
import { useIsSICore, useIsSIWG } from '~/utilities/useIsSI';
import type { NavInterface } from '~/types/AppData/Layout/NavInterface';

const headerClasses = cva(
  'fixed flex gap-2.5 items-center space-between w-full top-f-2 lg:top-f-1.5 h-12 px-4 z-50',
  {
    variants: {
      color: getCSSColorBg(),
      heightSm: getCSSHeightSmFactor(),
      heightMd: getCSSHeightMdFactor(),
      heightLg: getCSSHeightLgFactor(),
      hasThirdRow: {
        false: 'shadow-header',
      },
    },
  }
);

const navClasses = cva(
  'w-full h-full flex justify-center [&_.megamenu-link]:text-sm [&_li]:whitespace-nowrap',
  {
    variants: {
      isSICore: {
        true: '[&_.nav-item-link]:text-lg [&_.nav-item-link]:font-[400] pl-0',
        false: 'overflow-hidden',
      },
    },
  }
);

const mobileNavClasses = cva(
  '[&_.mobile-nav-top]:leading-[33px] [&_a]:uppercase [&_.mobile-nav-sub]:text-[14px]',
  {
    variants: {
      isSICore: {
        true: '[&_.mobile-nav-top]:text-[22px] [&_.mobile-nav-top]:font-big [&_.mobile-nav-top]:font-[600]',
        false: '',
      },
    },
  }
);

export default component$((): JSX.Element => {
  const {
    payloadToSSR: {
      layout: { subHeader, thirdHeader },
    },
    themeComponents: { threeRowsHeader },
  } = useContext(AppDataContext);
  const mobileActive = useSignal(false);
  const isSICore = useIsSICore();
  const isSIWG = useIsSIWG();

  if (!subHeader?.links?.value.length && !subHeader?.socialLinks?.value.length)
    return <></>;

  const {
    secondLineBackgroundColor,
    secondLineTextColor,
    secondRowLinkFontSizeLarge,
    secondRowLinkFontSizeMedium,
    secondRowLinkFontSizeSmall,
  } = threeRowsHeader;

  const NavThemeComponents: Partial<NavThemeComponentsInterface> = {
    LinkFontSizeLarge: {
      type: 'TYPOGRAPHY',
      value: secondRowLinkFontSizeLarge.value,
    },
    LinkFontSizeMedium: {
      type: 'TYPOGRAPHY',
      value: secondRowLinkFontSizeMedium.value,
    },
    LinkFontSizeSmall: {
      type: 'TYPOGRAPHY',
      value: secondRowLinkFontSizeSmall.value,
    },
    LinkColorTypeNormal: { type: 'COLOR', value: secondLineTextColor.value },
    LinkColorTypeHover: { type: 'COLOR', value: secondLineTextColor.value },
    LinkCurrentColor: { type: 'COLOR', value: secondLineTextColor.value },
    LinkColorTypeActive: { type: 'COLOR', value: secondLineTextColor.value },
    LinkActiveBorderBottom: { type: 'COLOR', value: secondLineTextColor.value },
    LinkHoverBorderBottom: { type: 'COLOR', value: secondLineTextColor.value },
    HoverOtherLinksColors: { type: 'COLOR', value: secondLineTextColor.value },
    LinkDropdownIconColor: { type: 'COLOR', value: secondLineTextColor.value },
    SocialLinksColor: { type: 'COLOR', value: secondLineTextColor.value },
    SocialLinksHoverColor: { type: 'COLOR', value: secondLineTextColor.value },
    SubMenuLinksColorTypeNormal: { type: 'COLOR', value: 'black' },
    SubMenuLinksColorTypeHover: { type: 'COLOR', value: 'black' },
  };

  // SI Header is not built with the theming tool, so we need to hardcode these values
  const navTheme = { ...SI_PRIMARY_NAV_THEME, ...NavThemeComponents };

  const context = {
    header: subHeader,
    theme: navTheme,
    hasAccessibility: false,
    navInMobile: false,
    canHaveMore: false,
    grow: false,
    scrollable: !isSICore,
    maskImage: 'none' as NavInterface['maskImage'],
  };

  if (!isSIWG) {
    context.maskImage = 'si';
  }

  useContextProvider(NavContext, context);

  return (
    <div
      class={headerClasses({
        color: secondLineBackgroundColor.value,
        hasThirdRow: !!thirdHeader,
      })}
    >
      <div class='flex items-center gap-3 lg:gap-0 h-full'>
        <div class={mobileNavClasses({ isSICore })}>
          <MobileIconSI
            mobileActive={mobileActive}
            fill={secondLineTextColor.value}
          />
          <MobileNav mobileActive={mobileActive} headerHeightOverwrite='88px' />
        </div>
        <LogoImageSI />
      </div>
      <div class={navClasses({ isSICore })}>
        <Nav />
      </div>
      <div class='flex items-center gap-4 h-full'>
        <SearchButton color={secondLineTextColor.value} />
        <AccessibilityButton color={secondLineTextColor.value} />
        <LanguagePicker />
      </div>
    </div>
  );
});
